import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { getApi } from "../../../api/api";
import { SaleItemInterface } from "../../../common/types/interfaces";
import { selectCity } from "../../../store/slices/citySlice";
import { useScrollPagination } from "../../../common/hooks/useScrollPagination";

const PAGE_LIMIT = 20;

export const useFavorite = () => {
  const api = getApi();
  const selectedCity = useSelector(selectCity);
  const cityId = selectedCity?.id;

  const [items, setItems] = useState<SaleItemInterface[]>([]);
  const [ready, setReady] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const totalRef = useRef(0);

  const loadPage = useCallback(
    async (nextOffset: number, reset = false) => {
      try {
        if (reset) setIsFetchingMore(false);
        else setIsFetchingMore(true);

        const result = await api.getFavorites({
          limit: PAGE_LIMIT,
          offset: nextOffset,
          cityId
        });

        const data = result.data || [];
        totalRef.current = result.total ?? data.length;

        setItems((prev) => (reset ? data : [...prev, ...data]));
        setOffset(nextOffset + data.length);
        setHasMore(nextOffset + data.length < totalRef.current);
      } catch {
        if (reset) setItems([]);
        setHasMore(false);
      } finally {
        setReady(true);
        setIsFetchingMore(false);
      }
    },
    [api, cityId],
  );

  useEffect(() => {
    setReady(false);
    setItems([]);
    setOffset(0);
    setHasMore(true);
    loadPage(0, true);
  }, [cityId, loadPage]);

  const loadMore = useCallback(() => {
    if (isFetchingMore || !hasMore) return;
    loadPage(offset);
  }, [isFetchingMore, hasMore, offset, loadPage]);

  const { lastItemRef } = useScrollPagination(hasMore, isFetchingMore, loadMore);

  const removeFavorite = useCallback(
    async (itemId: number) => {
      setRemovingId(itemId);
      try {
        await api.toggleFavorite(itemId);
        setItems((prev) => prev.filter((i) => i.id !== itemId));
        totalRef.current = Math.max(0, totalRef.current - 1);
        setHasMore(offset < totalRef.current);
      } finally {
        setRemovingId(null);
      }
    },
    [api, offset],
  );

  const available = items.filter((i) => i.isAvailableInSelectedCity !== false);
  const unavailable = items.filter((i) => i.isAvailableInSelectedCity === false);

  return {
    items,
    available,
    unavailable,
    ready,
    removingId,
    isFetchingMore,
    hasMore,
    lastItemRef,
    removeFavorite,
  };
};